import { RefreshIcon } from "@heroicons/react/outline";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { OAuthError, postData } from "../backend";
import { useModal, WorkflowContext } from "../contexts";
import WorkflowFormResponse from "./WorkflowFormResponse";
import OAuthModal from "./OAuthModal";
import { useTranslation } from "react-i18next";

function WorkflowFormResponseList(props: React.HTMLProps<HTMLDivElement>) {

  const { workflow } = useContext(WorkflowContext);
  const source = workflow.source;
  const { t } = useTranslation();
  const { openModal } = useModal();

  const [responses, setResposnes] = useState<Array<any>>();
  const [questions, setQuestions] = useState<Record<string, string>>();

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [retryToken, setRetryToken] = useState(0);

  const getResponses = useCallback((async () => {
    const formId = source?.file?.id;

    try {
      const token = await postData("/auth/access-token/", {});

      const responsesRes = await fetch(`https://forms.googleapis.com/v1/forms/${formId}/responses/`, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (responsesRes.ok) {
        const json = await responsesRes.json();
        const responses: Array<any> = json.responses ?? [];

        responses.sort((a, b) => Date.parse(b.createTime) - Date.parse(a.createTime));
        setResposnes(responses);
      }

      const formRes = await fetch(`https://forms.googleapis.com/v1/forms/${formId}/`, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (formRes.ok) {
        const json = await formRes.json();
        const items: Array<any> = json.items;

        const questionMap: Record<string, string> = {};

        for (const item of items) {
          const questionItem = item.questionItem;
          if (!questionItem)
            continue;

          const question = questionItem.question;
          if (!question)
            continue;

          questionMap[question.questionId] = item.title;
        }

        setQuestions(questionMap);
      }
    } catch (error) {
      // A 403 from the access-token request means we need broader Google scopes;
      // surface the OAuth modal so the user can re-authorise instead of hanging.
      if (error instanceof OAuthError)
        openModal(<OAuthModal oauthUrl={error.oauthUrl} onClose={() => { setIsLoading(true); setRetryToken(token => token + 1); }} />);
    } finally {
      // Always clear the loader so a failed token/fetch falls back to the
      // "Unable to load responses" state rather than spinning forever.
      setIsLoading(false);
    }
  }), [source?.file?.id, openModal]);

  useEffect(() => {
    if (responses === undefined)
      getResponses();
  }, [responses, getResponses, retryToken])


  function handleClickRefresh() {
    setIsRefreshing(true);
    getResponses()
      .finally(() => setIsRefreshing(false));
  }

  return (
    <div {...props}>
      {(responses === undefined && isLoading) &&
        <div className="flex flex-col justify-center items-center gap-2 w-full h-full">
          <img src="https://static.portant.co/portant-loading-gray.svg" className="w-24 h-24" alt="Loading Logo" />
          <div className="flex font-gilroy font-semibold text-gray-400">
            {t("Loading Google Form Responses...")}
          </div>
        </div>
      }

      {(responses === undefined && !isLoading) &&
        <div className="flex flex-col justify-center items-center gap-2 w-full h-full">
          <div className="flex font-gilroy font-semibold text-gray-400">
            {t("Unable to load responses")}
          </div>
        </div>
      }

      {responses !== undefined &&
        <div className="flex flex-col w-full h-full">
          <div className="flex items-end mx-4 px-4 pt-2 pb-1 border-b-2 border-gray-200 flex-shrink-0">
            <div className="font-gilroy font-semibold text-gray-600">
              {t("Form Responses")}
            </div>
            <RefreshIcon className={`h-6 w-6 ml-auto text-gray-400 hover:text-blue cursor-pointer ${isRefreshing ? "animate-spin" : "animate-none"}`} onClick={handleClickRefresh} />
          </div>
          <div className="flex flex-col h-full overflow-y-auto">
            {responses.length === 0 &&
              <div className="flex flex-col items-center justify-center flex-shrink-0 h-full">
                <div className="flex font-gilroy text-gray-600 font-semibold">
                  {t("No Responses")}
                </div>
                <div className="text-sm">
                  {t("Once a response is submitted to your form you will see it here")}
                </div>
              </div>
            }
            {responses.map((response, index) => <WorkflowFormResponse key={response.responseId} responseNumber={responses.length - index} response={response} questions={questions} />)}
          </div>
        </div>
      }
    </div>
  );

}

export default WorkflowFormResponseList;
